import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  User,
  Mail,
  Phone,
  Send,
  Building2,
  Briefcase,
  MapPin,
  MessageCircle,
  Bookmark,
  BookmarkCheck,
  BadgeCheck,
  ExternalLink,
  Lock,
  QrCode,
} from "lucide-react";
import { motion } from "motion/react";
import { useAppStore } from "../store/useAppStore";
import { companiesService } from "../services/companiesService";
import { catalogService } from "../services/catalogService";
import type { Company, Representative } from "../types";
import { maxPlatform } from "../services/maxPlatform";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { GlassCard } from "../components/GlassCard";
import { Button } from "../components/Button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { SkeletonList } from "../components/SkeletonList";
import { formatBoothLabel } from "../utils/locationLabel";
import { ScanNotesSection } from "../components/ScanNotesSection";
import { scanApi } from "../api/scan";
import { representativeSharePhone } from "@mvk/shared";

export function RepresentativeCardPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { saveContact, savedContacts, selectedEventId, isRepresentativeUnlocked, scanHistory } =
    useAppStore();
  const [representative, setRepresentative] = useState<Representative | undefined>();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [repScan, setRepScan] = useState<{ id: string; notes?: string; tags?: string[] } | null>(
    null,
  );

  useEffect(() => {
    if (!id || !selectedEventId) {
      setRepresentative(undefined);
      setCompany(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Pack-first O(1) lookup — never fan-out listRepresentatives across all companies.
        let rep = await catalogService.findRepresentative(selectedEventId, id);
        let companyId = rep?.companyId;

        if (!rep) {
          const fromHistory = scanHistory.find(
            (s) =>
              s.representativeId === id ||
              (s.scanTargetType === "representative" && s.scanTargetId === id) ||
              (s.scanTargetType === "badge" && s.scanTargetId === id),
          );
          companyId = fromHistory?.companyId ?? undefined;
          if (companyId) {
            const reps = await catalogService
              .listRepresentatives(selectedEventId, companyId)
              .catch(() => []);
            rep = reps.find((r) => r.id === id);
          }
        }

        if (!rep || !companyId) {
          if (!cancelled) {
            setRepresentative(undefined);
            setCompany(null);
          }
          return;
        }

        const comp = await companiesService
          .getById(companyId, selectedEventId)
          .catch(() => undefined);
        if (!cancelled) {
          setRepresentative(rep);
          setCompany(comp ?? null);
        }
      } catch {
        if (!cancelled) {
          setRepresentative(undefined);
          setCompany(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // scanHistory used only as fallback for companyId; pack lookup is primary.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-fetch when scanHistory reference churns
  }, [id, selectedEventId]);

  useEffect(() => {
    if (!id || !selectedEventId || !isRepresentativeUnlocked(id)) {
      setRepScan(null);
      return;
    }
    const matchRep = (s: {
      id: string;
      notes?: string;
      tags?: string[];
      representativeId?: string;
      scanTargetType?: string;
      scanTargetId?: string;
    }) =>
      s.representativeId === id ||
      (s.scanTargetType === "representative" && s.scanTargetId === id) ||
      (s.scanTargetType === "badge" && s.scanTargetId === id);

    // Prefer local history immediately (notes just saved in scan modal via updateScan).
    const local = scanHistory.find(matchRep);
    if (local) {
      setRepScan({ id: local.id, notes: local.notes, tags: local.tags });
    }

    let cancelled = false;
    (async () => {
      try {
        const { scans } = await scanApi.list(selectedEventId);
        if (cancelled) return;
        const hit = scans.find(matchRep);
        if (!hit) {
          if (!local) setRepScan(null);
          return;
        }
        // API wins when it has notes; otherwise keep local notes from this session.
        const notes =
          hit.notes?.trim() ? hit.notes : local?.notes?.trim() ? local.notes : hit.notes;
        const tags =
          hit.tags && hit.tags.length > 0 ? hit.tags : local?.tags?.length ? local.tags : hit.tags;
        setRepScan({ id: hit.id, notes, tags });
      } catch {
        if (!cancelled && !local) setRepScan(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scanHistory: read once per unlock/id; avoid churn
  }, [id, selectedEventId, isRepresentativeUnlocked]);

  const isSaved = useMemo(
    () => company ? savedContacts.some((c) => c.companyId === company.id) : false,
    [company, savedContacts]
  );

  if (loading) {
    return (
      <PageShell>
        <PageHeader title={t("common.loading")} back />
        <SkeletonList variant="detail" />
      </PageShell>
    );
  }

  if (!representative) {
    return (
      <PageShell>
        <div className="pt-20 text-center">
          <div className="text-gray-500 mb-4">{t("visitor.representative_not_found")}</div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            {t("common.back")}
          </Button>
        </div>
      </PageShell>
    );
  }

  const handleSaveContact = () => {
    if (!company) return;
    if (isSaved) {
      toast.info(t("visitor.contact_already_saved"));
      return;
    }
    maxPlatform.hapticFeedback("medium");
    saveContact({
      id: `sc-${Date.now()}`,
      userId: "user-1",
      eventId: company.eventId,
      companyId: company.id,
      savedAt: new Date().toISOString(),
    });
    toast.success(t("visitor.contact_saved"));
  };

  const handleOpenTelegram = () => {
    if (!representative.telegram) return;
    maxPlatform.hapticFeedback("medium");
    const username = representative.telegram.replace("@", "");
    window.open(`https://t.me/${username}`, "_blank");
  };

  const handleViewCompany = () => {
    if (company) {
      navigate(`/companies/${company.id}`);
    }
  };

  const hasTelegram = !!representative.telegram;
  const sharePhone = representativeSharePhone(representative);
  const hasPhone = !!sharePhone;
  const hasEmail = !!representative.email;
  const unlocked = isRepresentativeUnlocked(representative.id);

  return (
    <PageShell noPadding>
      <PageHeader
        title={t("visitor.personal_card")}
        subtitle={company?.name}
        back
        backTo={company ? `/companies/${company.id}` : "/companies"}
        right={
          unlocked ? (
            <span className="flex items-center gap-1 text-[11px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
              <BadgeCheck size={11} />
              {t("visitor.badge_scanned")}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              <Lock size={11} />
              {t("visitor.qr_required")}
            </span>
          )
        }
      />

      <div className="px-4 pt-6 pb-24 space-y-4">
        {/* Representative Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <GlassCard variant="elevated" padding="lg">
            <div className="flex flex-col items-center text-center">
              {/* Avatar */}
              {representative.photo ? (
                <img
                  src={representative.photo}
                  alt={representative.fullName}
                  className="w-24 h-24 rounded-full object-cover mb-4 ring-4 ring-indigo-100"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mb-4 ring-4 ring-indigo-100">
                  <User size={40} className="text-indigo-600" />
                </div>
              )}

              {/* Name & Position */}
              <h2 className="text-[22px] text-gray-900 mb-1">
                {representative.fullName}
              </h2>
              <p className="text-[14px] text-indigo-600 mb-2">
                {representative.position}
              </p>

              {/* Company link */}
              {company && (
                <button
                  onClick={handleViewCompany}
                  className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-indigo-600 transition-colors mb-2"
                >
                  <Building2 size={13} />
                  <span>{company.name}</span>
                  <span className="text-gray-300">·</span>
                  <span>{formatBoothLabel(company.booth)}</span>
                </button>
              )}

              {/* Bio — public */}
              {representative.bio && (
                <p className="text-[13px] text-gray-600 leading-relaxed mt-2 text-left w-full">
                  {representative.bio}
                </p>
              )}
            </div>

            {/* Primary CTA — Telegram only after badge scan */}
            {unlocked && hasTelegram && (
              <motion.button
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25 }}
                onClick={handleOpenTelegram}
                className="mt-5 w-full flex items-center justify-center gap-2.5 h-12 rounded-2xl bg-[#2AABEE] text-white active:bg-[#1d96d4] transition-colors shadow-lg shadow-sky-400/25"
              >
                <Send size={18} />
                <span className="text-[15px]">{t("visitor.write_telegram")}</span>
                <ExternalLink size={14} className="opacity-70" />
              </motion.button>
            )}
          </GlassCard>
        </motion.div>

        {/* Personal contacts — gated */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <GlassCard variant="default">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[14px] text-gray-900 flex items-center gap-2">
                <Briefcase size={15} className="text-indigo-500" />
                {t("visitor.personal_contacts")}
              </h3>
              {!unlocked && (
                <span className="flex items-center gap-1 text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                  <Lock size={10} />
                  {t("visitor.qr_required")}
                </span>
              )}
            </div>
            <p className="text-[12px] text-gray-400 mb-3 leading-snug">
              {unlocked
                ? t("visitor.personal_contacts_desc")
                : t("visitor.personal_contact_hint")}
            </p>

            {unlocked ? (
              <div className="space-y-2">
                {hasPhone && sharePhone && (
                  <a
                    href={`tel:${sharePhone}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Phone size={18} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-400 mb-0.5">{t("visitor.direct_phone")}</div>
                      <div className="text-[14px] text-gray-900">{sharePhone}</div>
                    </div>
                  </a>
                )}

                {hasEmail && (
                  <a
                    href={`mailto:${representative.email}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Mail size={18} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-400 mb-0.5">Email</div>
                      <div className="text-[14px] text-gray-900 truncate">{representative.email}</div>
                    </div>
                  </a>
                )}

                {hasTelegram && (
                  <button
                    onClick={handleOpenTelegram}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-sky-50 hover:bg-sky-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <Send size={18} className="text-sky-600" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[11px] text-sky-400 mb-0.5">Telegram</div>
                      <div className="text-[14px] text-sky-700">{representative.telegram}</div>
                    </div>
                    <ExternalLink size={14} className="text-sky-400 flex-shrink-0" />
                  </button>
                )}

                {!hasPhone && !hasEmail && !hasTelegram && (
                  <div className="text-center py-4 text-[13px] text-gray-400">
                    {t("visitor.contacts_missing")}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  maxPlatform.hapticFeedback("medium");
                  navigate("/scan");
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100 text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <QrCode size={18} className="text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-amber-900">{t("visitor.scan_badge_cta")}</div>
                  <div className="text-[12px] text-amber-700/80">{t("visitor.personal_contact_hint")}</div>
                </div>
              </button>
            )}
          </GlassCard>
        </motion.div>

        {/* Company Quick Info */}
        {company && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
          >
            <button onClick={handleViewCompany} className="w-full">
              <GlassCard variant="default" className="hover:border-indigo-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center flex-shrink-0">
                    <Building2 size={22} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[15px] text-gray-900 mb-0.5">{company.name}</div>
                    <div className="text-[12px] text-gray-500">{company.category}</div>
                    <div className="flex items-center gap-1 text-[12px] text-gray-400 mt-0.5">
                      <MapPin size={11} />
                      <span>{formatBoothLabel(company.booth)}</span>
                    </div>
                  </div>
                  <ChevronRightIcon />
                </div>
              </GlassCard>
            </button>
          </motion.div>
        )}

        {/* Notes after badge unlock */}
        {/* Notes only after badge/QR scan of this person. */}
        {unlocked && selectedEventId && repScan?.id ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.4 }}
          >
            <ScanNotesSection
              eventId={selectedEventId}
              scanId={repScan.id}
              rawNotes={repScan.notes}
              tags={repScan.tags}
              onSaved={({ scanId, notes, tags }) => {
                setRepScan({ id: scanId, notes, tags });
              }}
            />
          </motion.div>
        ) : null}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <button onClick={handleSaveContact} className="w-full">
            <GlassCard
              variant="subtle"
              padding="sm"
              className="active:scale-[0.98] transition-transform"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSaved ? "bg-emerald-100" : "bg-indigo-100"}`}>
                  {isSaved
                    ? <BookmarkCheck size={20} className="text-emerald-500" />
                    : <Bookmark size={20} className="text-indigo-500" />
                  }
                </div>
                <div className="flex-1 text-left">
                  <p className="text-[14px] text-gray-900">
                    {isSaved ? t("visitor.contact_saved") : t("visitor.save_contact")}
                  </p>
                  <p className="text-[12px] text-gray-500">
                    {isSaved ? t("companies.in_contacts") : t("visitor.add_company_favorite")}
                  </p>
                </div>
              </div>
            </GlassCard>
          </button>
        </motion.div>
      </div>
    </PageShell>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
